import React from "react"
import { Provider } from "react-redux"
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { createRoot } from "react-dom/client"

// core-js is a polyfill for various es6 and above features in IE11
// see : http://kangax.github.io/compat-table/es6/
// and: https://github.com/zloirock/core-js
import "core-js/stable"
import "regenerator-runtime/runtime"

import "./polyfills/intl-polyfills"

import RootContainer from "#/components/containers/RootContainer"
import "#/css/main.scss"
import { initializeErrorReporting } from "#/utils/ErrorReporting"
import { handleQueryError, registerQueryErrorStore } from "#/utils/queryErrorHandler"

import configureStore from "#/redux/configureStore"
import { BrowserRouter } from "react-router-dom"
import { ExtendedStringifyOptions, QueryParamProvider, transformSearchStringJsonSafe } from "use-query-params"
import { Route } from "react-router"
import { ConnectInsuranceContextProvider } from "./components/context/enrollment/ConnectInsuranceContext"
import SignUpFlowProvider from "#/components/context/signup/SignUpFlowContext"
// eslint-disable-next-line no-restricted-imports
import { CarrotThemeProvider, GlobalStyles } from "@carrotfertility/carotene" // please do not import from @carrotfertility/carotene after eslint rule added on 3/20/25
import { CssBaseline, ThemeProvider } from "@carrotfertility/carotene-core"
import { AuthenticationProvider } from "./components/context/authentication/AuthenticationProvider"
import { getHeap } from "#/utils/heap"
// @ts-expect-error TS(7016) FIXME: Could not find a declaration file for module '@kra... Remove this comment to see the full error message
import postRobot from "@krakenjs/post-robot"
import { isAndroidNativeApp, isIOSNativeApp } from "carrot-api/MobileNativeClient"
import {
  initSmartlingVisualContextCapture,
  shouldEnableSmartlingVisualContextCapture
} from "./utils/SmartlingVisualContextCapture"
import { IntlProviderWrapper } from "./components/context/user/LocaleContext"
import OpenIDLoginContainer from "#/components/containers/login/OpenIDLoginContainer"
import { Switch } from "#/services/routing/Switch"

const appBootDate = new Date()

initializeErrorReporting()

getHeap().addEventProperties({
  "React App Boot Timestamp": appBootDate.toISOString(),
  "React App Version": __APP_VERSION__ || "local",
  "Is iOS Native Mobile App": isIOSNativeApp() ? "true" : "false",
  "Is Android Native Mobile App": isAndroidNativeApp() ? "true" : "false"
})

getHeap().track("React App Booted", {
  timestamp: appBootDate.toISOString(),
  protocol: window?.location?.protocol
})

if (shouldEnableSmartlingVisualContextCapture()) {
  initSmartlingVisualContextCapture()
}

// this is referenced in the modal object TODO: move this to a config file
const applicationNode = "root"

const store = configureStore()
registerQueryErrorStore(store)

const queryStringifyOptions: ExtendedStringifyOptions = {
  transformSearchString: transformSearchStringJsonSafe
}

export const queryClient = new QueryClient({
  // Bridge 401s from both queries and mutations into the redux
  // SESSION_EXPIRED flow; the old ProviderFinderClient handled both.
  queryCache: new QueryCache({ onError: handleQueryError }),
  mutationCache: new MutationCache({ onError: handleQueryError })
})

if (window.self !== window.parent) {
  // @ts-expect-error TS(7006) FIXME: Parameter 'err' implicitly has an 'any' type.
  postRobot.send(window.parent, "memberAppLoaded").catch(function (err) {
    // eslint-disable-next-line no-console
    console.log(err)
  })
}

const root = createRoot(document.getElementById(applicationNode))

root.render(
  <CarrotThemeProvider>
    <ThemeProvider>
      <CssBaseline />
      <QueryClientProvider client={queryClient}>
        <Provider store={store}>
          <BrowserRouter>
            <QueryParamProvider ReactRouterRoute={Route} stringifyOptions={queryStringifyOptions}>
              <AuthenticationProvider>
                <SignUpFlowProvider>
                  <ConnectInsuranceContextProvider>
                    <IntlProviderWrapper>
                      <GlobalStyles />
                      <Switch>
                        <Route path="/openid-login" component={OpenIDLoginContainer} />
                        <Route render={() => <RootContainer />} />
                      </Switch>
                    </IntlProviderWrapper>
                  </ConnectInsuranceContextProvider>
                </SignUpFlowProvider>
              </AuthenticationProvider>
            </QueryParamProvider>
          </BrowserRouter>
        </Provider>
      </QueryClientProvider>
    </ThemeProvider>
  </CarrotThemeProvider>
)
